import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { User, Briefcase, Users, Mail, FileText } from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import {
  useCreateSpeaker,
  useUpdateSpeaker
} from '@utils/api/organizations/speakers';
import {
  Speaker,
  CreateSpeakerPayload
} from '@utils/types/organization/speaker.ts';
import { VoiceSamplesManager } from '@components/speakers/voice-samples-manager.tsx';
interface SpeakerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  speaker?: Speaker | null;
}

interface FormData {
  name: string;
  role?: string;
  party?: string;
  email?: string;
  notes?: string;
}

export default function SpeakerFormModal({
  isOpen,
  onClose,
  speaker
}: SpeakerFormModalProps) {
  const { t } = useTranslation();
  const isEditing = !!speaker;

  const createMutation = useCreateSpeaker();
  const updateMutation = useUpdateSpeaker();

  const schema = Yup.object().shape({
    name: Yup.string()
      .required(t('speaker-name-is-required'))
      .max(100, t('speaker-name-max-length')),
    role: Yup.string().max(100, t('speaker-role-max-length')),
    party: Yup.string().max(100, t('speaker-party-max-length')),
    email: Yup.string().email(t('invalid-email')),
    notes: Yup.string().max(500, t('speaker-notes-max-length'))
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      role: '',
      party: '',
      email: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (isOpen) {
      if (speaker) {
        reset({
          name: speaker.name || '',
          role: speaker.role || '',
          party: speaker.party || '',
          email: speaker.email || '',
          notes: speaker.notes || ''
        });
      } else {
        reset({
          name: '',
          role: '',
          party: '',
          email: '',
          notes: ''
        });
      }
    }
  }, [isOpen, speaker, reset]);

  const onSubmit = async (data: FormData) => {
    const payload: CreateSpeakerPayload = {
      name: data.name.trim(),
      // Backend types role/party as non-nullable str (default ""); sending null
      // 422s. Empty string is the correct "no role/party" value.
      role: data.role?.trim() || '',
      party: data.party?.trim() || '',
      email: data.email?.trim() || null,
      notes: data.notes?.trim() || null
    };

    try {
      if (isEditing && speaker) {
        await updateMutation.mutateAsync({
          speakerId: speaker.id,
          payload
        });
        toast.success(t('speaker-updated-successfully'));
      } else {
        // Backend POST /organization/speakers is idempotent (api-scriptix #3785):
        // 201 → a new row was created; 200 → an existing (name, role, party)
        // was returned unchanged. On the explicit admin form we surface the
        // difference so the user isn't told "created" when nothing was added
        // to the list.
        const { created } = await createMutation.mutateAsync(payload);
        if (created) {
          toast.success(t('speaker-created-successfully'));
        } else {
          toast(t('speaker-already-exists-no-changes'));
        }
      }
      onClose();
    } catch {
      // No local error toast — the global react-query mutation handler already
      // surfaces the BE's error_description / detail. Adding a generic
      // "Failed to create speaker" here just double-stacks on top of the
      // specific message the user actually wants to see.
    }
  };

  const isLoading =
    isSubmitting || createMutation.isLoading || updateMutation.isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('edit-speaker') : t('add-speaker')}
      caption={
        isEditing
          ? t('update-speaker-details')
          : t('add-a-new-speaker-to-directory')
      }
      customWidth="580px"
      footerActions={[
        <Button
          key="cancel"
          variant="secondary"
          onClick={onClose}
          label={t('cancel')}
          disabled={isLoading}
        />,
        <Button
          key="submit"
          onClick={handleSubmit(onSubmit)}
          label={isEditing ? t('save-changes') : t('create-speaker')}
          loading={isLoading}
          disabled={isLoading}
        />
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Name field - Primary field with emphasis */}
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={
                <span className="flex items-center gap-2">
                  <User size={14} />
                  {t('name')}
                </span>
              }
              placeholder={t('enter-speaker-name')}
              error={errors.name?.message}
              helperText={t('speaker-name-helper')}
              required
              autoFocus
            />
          )}
        />

        {/* Role and Party in a two-column layout */}
        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={
                  <span className="flex items-center gap-2">
                    <Briefcase size={14} />
                    {t('role')}
                  </span>
                }
                placeholder={t('enter-speaker-role')}
                error={errors.role?.message}
                helperText={t('speaker-role-helper')}
              />
            )}
          />

          <Controller
            name="party"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={
                  <span className="flex items-center gap-2">
                    <Users size={14} />
                    {t('party')}
                  </span>
                }
                placeholder={t('enter-speaker-party')}
                error={errors.party?.message}
                helperText={t('speaker-party-helper')}
              />
            )}
          />
        </div>

        {/* Email field */}
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={
                <span className="flex items-center gap-2">
                  <Mail size={14} />
                  {t('email')}
                </span>
              }
              placeholder={t('enter-speaker-email')}
              error={errors.email?.message}
              type="email"
            />
          )}
        />

        {/* Notes field - Multiline */}
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={
                <span className="flex items-center gap-2">
                  <FileText size={14} />
                  {t('notes')}
                </span>
              }
              placeholder={t('enter-speaker-notes')}
              error={errors.notes?.message}
              multiline
              helperText={t('speaker-notes-helper')}
            />
          )}
        />
      </form>

      {/* #2355 — voice samples belong to an existing speaker, so this only
          appears when editing one. The component hides itself for non-admins
          and when the organization has no samples stored. */}
      {isEditing && speaker && (
        <div className="mt-5">
          <VoiceSamplesManager
            speakerId={speaker.id}
            speakerName={speaker.name}
          />
        </div>
      )}
    </Modal>
  );
}
